import { AirbridgeHelper } from 'core/airbridge'
import { MarketingGTMHelper } from 'core/marketingGTM'
import { STORE_AIRBRIDGE, STORE_MARKETING_GTM } from 'core/store/analysis'
import { TypeConfig } from 'core/style/theme'
import { getExecutorForPartner } from 'core/util/partner'
import { useCallback, useEffect, useState } from 'react'
import { useSetRecoilState } from 'recoil'
import { datadogLogs } from '@datadog/browser-logs'
import { checkIsLocalhost } from 'core/util/url'

export default function useAnalysis({
  config,
  partnerId,
}: {
  config: TypeConfig
  partnerId?: string
}) {
  const setAirBridge = useSetRecoilState(STORE_AIRBRIDGE)
  const setMarketingGTM = useSetRecoilState(STORE_MARKETING_GTM)

  const [isAirbridgeInitialized, setIsAirbridgeInitialized] = useState<boolean>(false)
  const [isAirbridgeScriptLoaded, setIsAirbridgeScriptLoaded] = useState<boolean>(false)

  const [isMarketingGTMInitialized, setIsMarketingGTMInitialized] = useState<boolean>(false)
  const [isGoogleAnalyticsScriptLoaded, setIsGoogleAnalyticsScriptLoaded] = useState<boolean>(false)
  const [isGTMScriptLoaded, setIsGTMScriptLoaded] = useState<boolean>(false)
  const [isGTMNoScriptLoaded, setIsGTMNoScriptLoaded] = useState<boolean>(false)
  const [isGtagDataLayerScriptLoaded, setIsGtagDataLayerScriptLoaded] = useState<boolean>(false)

  const initializeAirbridge = ({ appName, token }: { appName?: string; token?: string }) => {
    if (!appName || !token) return
    const scriptEl = document.createElement('script')
    scriptEl.textContent = `
      <!-- Air Bridge -->
      (function(a_,i_,r_,_b,_r,_i,_d,_g,_e){if(!a_[_b]){var m=function(){var c=i_.createElement(r_);c.onerror=function(){f.queue.filter(function(b){return 0<=_d.indexOf(b[0])}).forEach(function(b){b=b[1];b=b[b.length-1];"function"===typeof b&&b("error occur when load airbridge")})};c.async=1;c.src=_r;"complete"===i_.readyState?i_.head.appendChild(c):i_.addEventListener("readystatechange",function g(){"complete"===i_.readyState&&(i_.removeEventListener("readystatechange",g),i_.head.appendChild(c))})},f={queue:[],get isSDKEnabled(){return!1}};_i.concat(_d).forEach(function(c){var b=c.split("."),g=b.pop();b.reduce(function(n,p){return n[p]=n[p]||{}},f)[g]=function(){f.queue.push([c,arguments])}});a_[_b]=f;0<_g?(a_=new (a_.XDomainRequest||a_.XMLHttpRequest),_b=function(){},a_.open("GET",_r),a_.timeout=_g,a_.onload=function(){m()},a_.onerror=_b,a_.onprogress=_b,a_.ontimeout=_b,a_.send()):m()}})(window,document,"script","airbridge","//static.airbridge.io/sdk/latest/airbridge.min.js","init startTracking fetchResource setBanner setDownload setDownloads openDeeplink setDeeplinks sendWeb setUserAgent setUserAlias addUserAlias setMobileAppData setUserId setUserEmail setUserPhone setUserAttributes clearUser setDeviceAlias removeDeviceAlias clearDeviceAlias setDeviceIFV setDeviceIFA setDeviceGAID events.send events.signIn events.signUp events.signOut events.purchased events.addedToCart events.productDetailsViewEvent events.homeViewEvent events.productListViewEvent events.searchResultViewEvent".split(" "),["events.wait","createTouchpoint"],0);
      airbridge.init({
        app: '${appName}',
        webToken: '${token}'
      });
      <!-- End Air Bridge -->
    `
    document.head.appendChild(scriptEl)
    setIsAirbridgeScriptLoaded(true)
  }

  const initializeGTM = ({
    gtmId,
    gtmCustomUrl,
    gtagId,
  }: {
    gtmId?: string
    gtmCustomUrl?: string
    gtagId?: string
  }) => {
    if (!gtmId) return

    // 크로스도메인용 _gl 생성 목적의 라이브러리
    const googleAnalyticsScriptEl = document.createElement('script')
    googleAnalyticsScriptEl.src = 'https://www.google-analytics.com/analytics.js'
    document.head.append(googleAnalyticsScriptEl)
    googleAnalyticsScriptEl.addEventListener('load', () => setIsGoogleAnalyticsScriptLoaded(true))

    const gtmUrl = gtmCustomUrl ?? 'https://www.googletagmanager.com'
    const gtmScriptEl = document.createElement('script')
    gtmScriptEl.textContent = `
      try {
        <!-- Google Tag Manager -->
        (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start': new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='${gtmUrl}/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${gtmId}');
        <!-- End Google Tag Manager -->
      } catch (e) {
        console.log('Marketing GTM script initialing error', e);
      }
    `
    document.head.appendChild(gtmScriptEl)
    setIsGTMScriptLoaded(true)

    const gtmNoScriptEl = document.createElement('noscript')
    gtmNoScriptEl.textContent = `
      try {
        <!-- Google Tag Manager (noscript) -->
        <iframe src="${gtmUrl}/ns.html?id=${gtmId}" height="0" width="0" style="display:none;visibility:hidden"></iframe>
        <!-- End Google Tag Manager (noscript) -->
      } catch (e) {
        console.log('Marketing GTM noscript initialing error', e);
      }
    `
    document.body.prepend(gtmNoScriptEl)
    setIsGTMNoScriptLoaded(true)

    if (!gtagId) return
    const gtagScriptEl = document.createElement('script')
    gtagScriptEl.setAttribute('async', '')
    gtagScriptEl.src = `https://www.googletagmanager.com/gtag/js?id=${gtagId}`
    document.head.appendChild(gtagScriptEl)
    const gtagDataLayerScriptEl = document.createElement('script')
    gtagDataLayerScriptEl.textContent = `
      try {
        window.dataLayer = window.dataLayer || [];function gtag(){dataLayer.push(arguments);}gtag("js", new Date());gtag("config", "${gtagId}", { "send_page_view": false });
      } catch (e) {
        console.log('Marketing GTM Gtag script initialing error', e);
      }
    `
    document.head.appendChild(gtagDataLayerScriptEl)
    setIsGtagDataLayerScriptLoaded(true)
  }

  const initializeDatadog = () => {
    const clientToken = process.env.REACT_APP_DATADOG_CLIENT_TOKEN

    if (!clientToken) return

    datadogLogs.init({
      clientToken,
      env: process.env.REACT_APP_ENV,
      forwardErrorsToLogs: true,
      service: 'saucelive-collect-frontend',
      sessionSampleRate: Number(process.env.REACT_APP_DATADOG_LOGS_SESSION_SAMPLE_RATE),
      site: 'datadoghq.com',
      silentMultipleInit: true,
    })
  }

  const initializeAnalysis = useCallback(() => {
    const {
      gtagId: marketingGTMGtagId,
      gtmId: marketingGTMId,
      use: isUseMarketingGTM,
    } = config?.marketingGTM ?? {}
    isUseMarketingGTM &&
      initializeGTM({
        gtagId: marketingGTMGtagId ?? undefined,
        gtmCustomUrl: getExecutorForPartner(partnerId)?.gtmCustomUrl,
        gtmId: marketingGTMId ?? undefined,
      })

    const {
      id: airbridgeToken,
      name: airbridgeAppName,
      use: isUseAirbridge,
    } = config?.airbridge ?? {}
    isUseAirbridge &&
      initializeAirbridge({
        appName: airbridgeAppName ?? undefined,
        token: airbridgeToken ?? undefined,
      })

    if (!checkIsLocalhost()) {
      initializeDatadog()
    }
  }, [config.airbridge, config.marketingGTM, partnerId])

  /**
   * 마케팅GTM 초기화 여부 확인 useEffect
   */
  useEffect(() => {
    setIsMarketingGTMInitialized(
      isGoogleAnalyticsScriptLoaded &&
        isGtagDataLayerScriptLoaded &&
        isGTMNoScriptLoaded &&
        isGTMScriptLoaded,
    )
  }, [
    isGoogleAnalyticsScriptLoaded,
    isGtagDataLayerScriptLoaded,
    isGTMNoScriptLoaded,
    isGTMScriptLoaded,
  ])

  /**
   * 마케팅GTM 헬퍼 초기화 useEffect
   */
  useEffect(() => {
    if (!isMarketingGTMInitialized) return
    const { marketingGTM } = config
    setMarketingGTM(new MarketingGTMHelper(marketingGTM.gtagId))
  }, [config, isMarketingGTMInitialized, setMarketingGTM])

  /**
   * 에어브릿지 초기화 여부 확인 useEffect
   */
  useEffect(() => {
    setIsAirbridgeInitialized(isAirbridgeScriptLoaded)
  }, [isAirbridgeScriptLoaded])

  /**
   * 에어브릿지 헬퍼 초기화 useEffect
   */
  useEffect(() => {
    if (!isAirbridgeInitialized) return

    setAirBridge(new AirbridgeHelper())
  }, [isAirbridgeInitialized, setAirBridge])

  /**
   * 초기화 실행 useEffect
   */
  useEffect(() => {
    initializeAnalysis()
  }, [initializeAnalysis])
}
